import { Progress } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import type { ProgressStyle, Task } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { erasedOr } from '@/lib/erased';

/**
 * Where the applicant stands, in the style the studio chose (OL-24). COUNT is the room's long-standing
 * line. An optional step left undone counts for nothing, matching the server's overall status.
 */
export function JourneyProgress({ tasks, style }: { tasks: Task[]; style: ProgressStyle }) {
  const { t } = useI18n();
  if (style === 'NONE') return null;
  const counted = tasks.filter((x) => !x.optional || x.status !== 'PENDING');
  const done = counted.filter((x) => x.status !== 'PENDING').length;
  const label = t('tasks.progress', { done, total: counted.length });

  if (style === 'BAR') {
    return (
      <div className="dr-progress" data-testid="progress-bar" style={{ marginBottom: 14 }}>
        <Progress
          percent={counted.length ? Math.round((done / counted.length) * 100) : 0}
          showInfo={false}
          aria-label={label}
        />
        <div className="dr-meta">{label}</div>
      </div>
    );
  }
  if (style === 'STEPS') {
    return (
      <div style={{ marginBottom: 14 }}>
        <ol className="dr-progress-steps" data-testid="progress-steps" aria-label={label}>
          {tasks.map((x, i) => {
            const isDone = x.status !== 'PENDING';
            return (
              <li
                key={x.taskId}
                className={`dr-progress-step${isDone ? ' dr-progress-step--done' : ''}`}
                title={erasedOr(x.title, t('common.erased'))}
              >
                {isDone ? <CheckOutlined aria-hidden="true" /> : i + 1}
              </li>
            );
          })}
        </ol>
        <div className="dr-meta">{label}</div>
      </div>
    );
  }
  return (
    <div className="dr-meta" data-testid="progress-count" style={{ marginBottom: 14 }}>
      {label}
    </div>
  );
}
